import type { HudIcon } from '../icons.js';
import { abilityIcon } from '../content-icons.js';
import { ACTION_CAST_SECONDS, type AbilityTarget, ClassAbilityDefinition } from '../../../shared/game/class-types.js';
import { HERO_ACTION_SECONDS } from '../../../shared/game/combat-types.js';
import { ANTICIPATE_COOLDOWN_SECONDS, HIDE_COOLDOWN_SECONDS, RUN_COOLDOWN_SECONDS, TACTICAL_ACTIONS, type TacticalActionDefinition } from '../../../shared/game/tactical-types.js';
import { autocasting, type BehaviorSettings } from '../../../shared/game/behavior-types.js';
import type { GameContent } from '../../../shared/game/world-types.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';

export interface AbilityView {
  heroId: string; heroName: string; color: string; abilityId: string; name: string; description: string;
  target: AbilityTarget; cost: string; uses: string; disabled: string; maxTargets?: number;
  /** An ally power that reaches only Dying allies. */
  dyingTarget?: boolean;
  /** A healing touch: reaches any living party member, the caster included. */
  healTarget?: boolean;
  /** A stance that is currently held. */
  active: boolean; icon: HudIcon;
  /** Whether the AI may use it by itself; null when it has no autocast. */
  autocast: boolean | null;
  /** An attack cantrip: switching its autocast on makes it the hero's autoattack and switches the other cantrips off. */
  autoAttack?: boolean;
  /** A spell, listed apart from the class's other powers. */
  spell?: boolean;
  /** One of the tactical actions every hero has (Hide, Run, Assess…). */
  tactic?: boolean;
}
/** The behavior change that flips a slot's autocast. */
export function autocastPatch(view: AbilityView, behavior: Pick<BehaviorSettings, 'autocastOff'>): Partial<BehaviorSettings> {
  if (view.autoAttack) return { autoAttack: view.autocast ? null : view.abilityId };
  return { autocastOff: view.autocast ? [...behavior.autocastOff, view.abilityId] : behavior.autocastOff.filter(id => id !== view.abilityId) };
}
const STANCE_COOLDOWN_SECONDS: Partial<Record<string, number>> = { 'anticipate-danger': ANTICIPATE_COOLDOWN_SECONDS, hide: HIDE_COOLDOWN_SECONDS, run: RUN_COOLDOWN_SECONDS };
const NO_ACTION = 'No action available yet.', ALREADY_USED = 'Already used this encounter.';
/** The hero voice event for a refused press: a hero with no action pip to spend, or a power that isn't ready. Other refusals (fallen, not in a fight) say nothing. */
export function refusalVoiceEvent(disabled: string): 'no-actions' | 'cooldown' | null {
  if (disabled === NO_ACTION) return 'no-actions';
  return disabled === ALREADY_USED || disabled.startsWith('Ready in ') ? 'cooldown' : null;
}

/** One entry per active power of each listed hero. Passive features never appear. The server decides every rule; this mirrors it to disable menu entries. */
export function abilityViews(snapshot: WorldSnapshot, content: GameContent, heroIds: string[]): AbilityView[] {
  const views: AbilityView[] = [];
  for (const heroId of heroIds) {
    const hero = snapshot.heroes.find(candidate => candidate.id === heroId);
    const definition = content.heroes.find(candidate => candidate.id === heroId);
    if (!hero || !definition) continue;
    const cls = content.classes?.find(candidate => candidate.id === hero.class.classId);
    for (const ability of cls?.abilities ?? []) {
      if (ability.reaction) continue;
      const state = hero.class.abilities.find(candidate => candidate.id === ability.id);
      if (!state) continue;
      views.push({ heroId, heroName: hero.name, color: '#' + definition.color.toString(16).padStart(6, '0'), abilityId: ability.id, name: ability.name, description: ability.description,
        ...(ability.maxTargets ? { maxTargets: ability.maxTargets } : {}), ...(ability.dyingTarget ? { dyingTarget: true } : {}), ...(ability.healTarget ? { healTarget: true } : {}),
        target: ability.target, cost: ability.actions ? `${ability.actions} action${ability.actions === 1 ? '' : 's'} · ${ability.actions * ACTION_CAST_SECONDS}s cast` : 'Free',
        uses: ability.usesPerEncounter !== undefined ? ` · ${state.charges}/${ability.usesPerEncounter}` : ability.usesPerSafeRest ? ` · ${state.charges} left`
          : ability.healingPool ? ` · ${state.charges}/${ability.healingPool.perLevel * hero.class.level} HP`
          : ability.summon && hero.class.summons ? ` · ${hero.class.summons.count}/${hero.class.summons.limit}`
          : ability.rage && hero.class.fury ? ` · ${hero.class.fury.dice.length}/${hero.class.fury.max}` : '',
        disabled: unavailable(snapshot, hero, ability, state.charges, state.readyIn),
        autocast: ability.autocast ? autocasting(hero.behavior, ability) : null, ...(ability.autoAttack ? { autoAttack: true } : {}), ...(ability.spell ? { spell: true } : {}),
        active: false, icon: abilityIcon(ability.id) });
    }
    for (const action of TACTICAL_ACTIONS) {
      const active = action.id === 'hide' ? hero.combat.hiding === true : action.id === 'run' ? hero.combat.running === true : action.stance === true && hero.combat.anticipating;
      views.push({ heroId, heroName: hero.name, color: '#' + definition.color.toString(16).padStart(6, '0'), abilityId: action.id, name: action.name, description: action.description,
        target: action.target, cost: action.stance ? `Free · ${STANCE_COOLDOWN_SECONDS[action.id] ?? ANTICIPATE_COOLDOWN_SECONDS}s cooldown` : `1 action · ${HERO_ACTION_SECONDS}s`, uses: '', disabled: tacticalUnavailable(snapshot, hero, action, active), active, icon: abilityIcon(action.id),
        autocast: action.autocast ? autocasting(hero.behavior, action) : null, tactic: true });
    }
  }
  return views;
}
function unavailable(snapshot: WorldSnapshot, hero: WorldSnapshot['heroes'][number], ability: ClassAbilityDefinition, charges: number, readyIn = 0): string {
  if (hero.health.dead) return 'This hero has fallen.';
  if (!hero.connected) return 'This hero is not connected.';
  if (ability.combatOnly && !snapshot.inCombat) return `${ability.name} can only be used in a fight.`;
  if (ability.usesPerEncounter !== undefined && charges < 1) return ALREADY_USED;
  if (readyIn > 0) return `Ready in ${readyIn.toFixed(1)}s.`;
  if (ability.usesPerSafeRest && charges < 1) return 'No uses left until a Safe Rest.';
  if (ability.healingPool && charges < 1) return 'No healing power left until a Safe Rest.';
  if (ability.summons && hero.class.companion) return 'Already at your side.';
  if (ability.companion && !hero.class.companion) return 'Summon the Lifebinding Spirit first.';
  if (ability.companion && hero.class.companion?.action) return 'The spirit is busy.';
  const summons = hero.class.summons;
  if (ability.summon && summons && summons.count >= summons.limit) return `Shadow limit reached (${summons.limit}).`;
  if (ability.id === 'command-shadows' && !summons?.count) return 'No Shadows to command.';
  if (ability.rage && hero.health.dying) return 'Cannot Rage while Dying.';
  if (ability.id === 'overload' && !hero.combat.charged) return 'Not Charged.';
  if (snapshot.inCombat && hero.combat.actions < ability.actions) return NO_ACTION;
  return '';
}

function tacticalUnavailable(snapshot: WorldSnapshot, hero: WorldSnapshot['heroes'][number], action: TacticalActionDefinition, active: boolean): string {
  if (hero.health.dead) return 'This hero has fallen.';
  if (!hero.connected) return 'This hero is not connected.';
  const cooldown = (action.id === 'assess' ? hero.combat.assessCooldown : action.id === 'hide' ? hero.combat.hideCooldown : action.id === 'run' ? hero.combat.runCooldown : action.stance ? hero.combat.anticipateCooldown : 0) ?? 0;
  if (cooldown > 0) return `Ready in ${cooldown.toFixed(1)}s.`;
  if (active || action.stance) return '';
  if (action.id === 'create-opening' && !snapshot.inCombat) return `${action.name} can only be used in a fight.`;
  if (snapshot.inCombat && hero.combat.actions < 1) return NO_ACTION;
  return '';
}
